import { Avatar, Box, Tag, TagCloseButton, TagLabel, Tooltip } from "@chakra-ui/react";
import { FiMail } from "react-icons/fi";

export type RecipientChipVariant = "user" | "role" | "email" | "empty";

interface SizeTokens {
  tagSize: "sm" | "md";
  px: number;
  pl: number;
  pr: number;
  py: number;
  fontSize: string;
  avatarBoxSize: string;
  avatarFontSize: string;
  gap: number;
}

const SIZE_TOKENS: Record<"xs" | "sm" | "md", SizeTokens> = {
  xs: { tagSize: "sm", px: 1, pl: 0.5, pr: 1.5, py: 0, fontSize: "10px", avatarBoxSize: "14px", avatarFontSize: "8px", gap: 1 },
  sm: { tagSize: "sm", px: 1, pl: 1, pr: 2, py: 0, fontSize: "12px", avatarBoxSize: "16px", avatarFontSize: "9px", gap: 1.5 },
  md: { tagSize: "md", px: 2, pl: 1.5, pr: 2.5, py: 0, fontSize: "14px", avatarBoxSize: "18px", avatarFontSize: "10px", gap: 2 },
};

interface RecipientChipProps {
  /** Visual variant — controls what's rendered to the left of the label. */
  variant: RecipientChipVariant;
  /** The text inside the chip. For `empty`, this is the placeholder ("—"). */
  label: string;
  /** `user` only — avatar image; falls back to initials. */
  avatarUrl?: string;
  /** `user` only — explicit initials (defaults to first letters of label). */
  initials?: string;
  /** `role` only — `{ bg, fg }` pair driving the chip's fill + circle. Required when variant="role" (non-empty roles). */
  roleColors?: { bg: string; fg: string };
  size?: "xs" | "sm" | "md";
  /** Add a 1px border in the chip's text color — useful on tinted surfaces (compose box header). */
  bordered?: boolean;
  /** When provided, renders an x button that fires this callback. */
  remove?: () => void;
  /** Role variant only — render just the avatar circle, no text. */
  iconOnly?: boolean;
}

function defaultInitials(label: string): string {
  return label.split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join("");
}

/**
 * The shared recipient/assignee chip used across the task grid, message
 * compose, and email compose surfaces. Pure styling primitive — feature
 * adapters (RoleLabelTag, EmailRecipientTag) map their domain types into
 * the right props.
 */
export function RecipientChip({
  variant,
  label,
  avatarUrl,
  initials,
  roleColors,
  size = "sm",
  bordered = false,
  remove,
  iconOnly = false,
}: RecipientChipProps) {
  const t = SIZE_TOKENS[size];

  if (variant === "empty") {
    return (
      <Tag size={t.tagSize} variant="subtle" px={t.px} fontSize={t.fontSize}>
        <TagLabel>{label}</TagLabel>
      </Tag>
    );
  }

  if (variant === "user") {
    return (
      <Tag
        size={t.tagSize}
        bg="cdsIndigo.50"
        pl={t.pl}
        pr={t.pr}
        py={t.py}
        border={bordered ? "1px solid" : undefined}
        borderColor={bordered ? "cdsIndigo.200" : undefined}
      >
        <Avatar
          boxSize={t.avatarBoxSize}
          name={label}
          src={avatarUrl}
          bg="cdsIndigo.300"
          color="white"
          getInitials={() => initials ?? defaultInitials(label)}
          mr={t.gap}
          sx={{ "& > div": { fontSize: t.avatarFontSize } }}
        />
        <TagLabel fontSize={t.fontSize} color="cdsIndigo.700">{label}</TagLabel>
        {remove && (
          <TagCloseButton aria-label="Remove" fontSize="12px" w="8px" onClick={remove} color="cdsIndigo.700" />
        )}
      </Tag>
    );
  }

  if (variant === "email") {
    return (
      <Tag
        size={t.tagSize}
        bg="cdsIndigo.50"
        pl={t.pl + 0.5}
        pr={t.pr}
        py={t.py}
        border={bordered ? "1px solid" : undefined}
        borderColor={bordered ? "cdsIndigo.200" : undefined}
      >
        <Box as={FiMail} boxSize={t.avatarBoxSize} color="cdsIndigo.700" mr={t.gap} />
        <TagLabel fontSize={t.fontSize} color="cdsIndigo.700">{label}</TagLabel>
        {remove && (
          <TagCloseButton aria-label="Remove" fontSize="12px" w="8px" onClick={remove} color="cdsIndigo.700" />
        )}
      </Tag>
    );
  }

  // variant === "role"
  const bg = roleColors?.bg ?? "gray.100";
  const fg = roleColors?.fg ?? "gray.700";

  if (iconOnly) {
    return (
      <Tooltip label={label} placement="top" hasArrow openDelay={300}>
        <Avatar
          boxSize={t.avatarBoxSize}
          name={label}
          bg={fg}
          color={bg}
          getInitials={(name) => name.charAt(0)}
          sx={{ "& > div": { fontSize: t.avatarFontSize, fontWeight: 700 } }}
        />
      </Tooltip>
    );
  }

  return (
    <Tag
      size={t.tagSize}
      bg={bg}
      color={fg}
      pl={t.pl}
      pr={t.pr}
      py={t.py}
      border={bordered ? "1px solid" : undefined}
      borderColor={bordered ? fg : undefined}
    >
      <Avatar
        boxSize={t.avatarBoxSize}
        name={label}
        bg={fg}
        color={bg}
        getInitials={(name) => name.charAt(0)}
        mr={t.gap}
        sx={{ "& > div": { fontSize: t.avatarFontSize } }}
      />
      <TagLabel fontSize={t.fontSize}>{label}</TagLabel>
      {remove && (
        <TagCloseButton aria-label="Remove" fontSize="12px" w="8px" onClick={remove} color={fg} />
      )}
    </Tag>
  );
}

export default RecipientChip;
